import { DateTime } from "luxon";

// Stored timestamps come back either as space-separated SQL text (Drizzle
// mode:"string" columns, e.g. "2026-08-01 18:30:00", no zone) or as ISO strings
// with an offset. Both represent a UTC instant, so try SQL first, then ISO.
export const parseStoredUtc = (value: string): DateTime => {
  const sql = DateTime.fromSQL(value, { zone: "utc" });
  return sql.isValid ? sql : DateTime.fromISO(value, { zone: "utc" });
};

// The inverse of parseStoredUtc: converts an instant to the UTC string those columns
// expect. Reach for this instead of .toSQL(), which serializes in the *server's* zone —
// the column carries no zone, so a local-time string is read back as UTC and the value
// silently shifts by the server's offset.
export const formatStoredUtc = (value: Date): string | null =>
  DateTime.fromJSDate(value).toUTC().toISO();

// formatStoredUtc for values arriving from a form. Date pickers submit an ISO
// string carrying the event's offset ("2026-12-01T17:00:00.000-05:00"); the
// zoneless column it lands in keeps the wall time and throws the offset away,
// so the value reads back hours off unless it is normalised on the way in.
export const toStoredTimestamp = (value: string | null | undefined): string | null => {
  if (!value?.trim()) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatStoredUtc(parsed);
};

export const formatDate = ({
  date,
  formatString = "D",
  formatTimezone = "America/New_York",
}: {
  date: string;
  formatString?: string;
  formatTimezone?: string;
}) => {
  return parseStoredUtc(date).setZone(formatTimezone).toFormat(formatString);
};

export type EventStatus = "live" | "upcoming" | "ended";

export const getEventStatus = ({
  startDate,
  endDate,
}: {
  startDate?: string | null;
  endDate?: string | null;
}): EventStatus => {
  const now = DateTime.utc();
  const start = startDate ? parseStoredUtc(startDate) : null;
  const end = endDate ? parseStoredUtc(endDate) : null;

  if (end?.isValid && end < now) return "ended";
  if (start?.isValid && start > now) return "upcoming";
  return "live";
};

export const formatRelativeToNow = (value?: string | null): string | null => {
  if (!value) return null;
  const dt = parseStoredUtc(value);
  if (!dt.isValid) return null;
  return dt.toRelative();
};

export const formatDateRange = ({
  dates: _dates,
  formatString = ["D"],
  formatTimezone = "America/New_York",
  joiningChar = " - ",
}: {
  dates: (string | null | undefined)[];
  formatString?: string[];
  formatTimezone?: string;
  joiningChar?: string;
}) => {
  const dates = _dates.filter((date) => date) as string[];
  const dateValues = dates?.map((date, index) =>
    formatDate({
      date,
      formatString: formatString[index] ?? formatString[0],
      formatTimezone,
    })
  );

  return dateValues.join(joiningChar);
};

export const getTimezoneAbbreviation = (
  ianaTimezone: string,
  referenceDate?: string,
): string => {
  const dt = referenceDate
    ? parseStoredUtc(referenceDate).setZone(ianaTimezone)
    : DateTime.local().setZone(ianaTimezone);

  if (!dt.isValid) return ianaTimezone;

  return dt.offsetNameShort || ianaTimezone;
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};
